import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/procedures.actions';
import { proceduresSelector } from 'redux/selectors/procedures.selector';
import { isClienteRole } from 'helpers/roles';
import { userSelector } from 'redux/selectors/auth.selector';

import LayoutContainer from 'containers/LayoutContainer';
import ProceduresView from 'views/ProceduresView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IProcedure } from 'types/procedures.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/ProceduresView/i18n';

const ProceduresListContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IProcedure | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.procedures);
  const items = useTypedSelector(proceduresSelector);

  const getItem = (id: string) => items.find((item: IProcedure) => item.id === Number(id));
  const user = useTypedSelector(userSelector);

  const actions = (id: string) => {
    const item = getItem(id);
    if (!item) return [];
    const canEditOrDelete = !isClienteRole(user) || item?.state === 'OPENED';
    const availableActions = [
      {
        label: i18next.t<string>('PROCEDURES_VIEW:SHOW'),
        onClick: () => {
          window.open(item.url, '_blank');
        },
      },
    ];
    if (canEditOrDelete) {
      availableActions.push(
        {
          label: i18next.t<string>('PROCEDURES_VIEW:EDIT'),
          onClick: () => {
            window.open(item.url.replace('/show', '/edit'), '_blank');
          },
        },
        {
          label: i18next.t<string>('PROCEDURES_VIEW:DELETE'),
          onClick: () => setDeleteItem(item),
        },
      );
    }

    return availableActions;
  };

  const columns = [
    {
      name: 'createdAt',
      label: i18next.t<string>('PROCEDURES_VIEW:DATE'),
      options: {
        customBodyRender: (value: string) => new Date(value).toLocaleDateString(),
        sort: true,
      },
    },
    {
      name: 'name',
      label: i18next.t<string>('PROCEDURES_VIEW:NAME'),
    },
    {
      name: 'formTypeName',
      label: i18next.t<string>('PROCEDURES_VIEW:TYPE'),
    },
    {
      name: 'stateName',
      label: i18next.t<string>('PROCEDURES_VIEW:STATE'),
    },
    {
      name: 'id',
      label: i18next.t<string>('PROCEDURES_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions(value)} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer isPublic title={i18next.t<string>('PUBLIC_PROCEDURES_VIEW:TITLE')}>
      <ProceduresView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('PROCEDURES_VIEW:DELETE_DIALOG_MESSAGE', {
          name: deleteItem?.name,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('PROCEDURES_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default ProceduresListContainer;
